import { ChevronDown, ChevronRight, LayoutGrid, List, Shield, ShieldCheck } from 'lucide-react';
import { useQueries } from '@tanstack/react-query';
import { useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useApi } from '../../api/context';
import { queryKeys, useNetworkKinds } from '../../api/queries';
import type { AddressPlan, Attachment, EntryPointStatus, Network, NetworkDns } from '../../api/types';
import { ErrorMessage, Loading, StatusBadge } from '../../components/ui';
import { usePhone } from '../../shell/breakpoints';
import { strings } from '../../strings';
import { attachmentPresentation, providerLabel, waitingWords } from '../shared';
import { AttachmentsTab } from './AttachmentsTab';
import { GettingStarted } from './GettingStarted';
import { SidePanel } from './SidePanel';
import {
  attachmentKey,
  buildTopology,
  cardStateWords,
  dnsSummary,
  ENTRY_POINTS_KEY,
  LIST_DEFAULT_ABOVE,
  providerRegions,
  totalAttachments,
  waitsForNetworkApply,
  zonesKey,
  topologyShape,
  type TopologyItem,
} from './topology';
import { TopologyTree } from './TopologyTree';

const p = strings.networks.page;
const tp = strings.networks.topology;

const LEGEND: { tone: string; label: string }[] = [
  { tone: 'success', label: strings.networks.attachmentState.connected },
  { tone: 'info', label: strings.networks.attachmentState.connecting },
  { tone: 'warning', label: strings.networks.attachmentState.pending },
  { tone: 'danger', label: strings.networks.attachmentState.failed },
];

/**
 * The Overview (spec 11 6.15.5, mocks n02, n08): Getting started while there are no attachments, the topology as a
 * diagram (one tree) or the attachments list, and the side panel of the selected item. Phones show the attachments
 * grouped by provider and region instead (6.15.19).
 */
export function NetworkOverview({
  network,
  attachments,
  attachmentsError,
  plan,
  dns,
  poll,
  showChecklist,
  busy,
  onCloseChecklist,
  onPlan,
  onAttach,
}: {
  network: Network;
  attachments: Attachment[] | undefined;
  attachmentsError: unknown;
  plan: AddressPlan | undefined;
  dns: NetworkDns | undefined;
  poll: number | false;
  showChecklist: boolean;
  busy: boolean;
  onCloseChecklist: () => void;
  onPlan: () => void;
  onAttach: () => void;
}) {
  const phone = usePhone();
  const [search, setSearch] = useSearchParams();
  const selected = search.get('select');
  const [view, setView] = useState<'diagram' | 'list' | null>(null);
  const [expandedGroups, setExpandedGroups] = useState<ReadonlySet<string>>(new Set());
  const focusItem = useRef<((id: string) => void) | null>(null);
  const panel = useRef<HTMLElement>(null);
  // Phase 3: the entry points' kinds and statuses (pending origins) for the band and the tree's names.
  const api = useApi();
  const eps = network.settings.entryPoints;
  const kinds = useNetworkKinds(network.id, eps.length > 0);
  const statusQueries = useQueries({
    queries: eps.map((x) => ({
      queryKey: queryKeys.entryPointStatus(network.id, x.id),
      queryFn: () => api.getEntryPointStatus(network.id, x.id),
    })),
  });
  const statusKey = statusQueries.map((q) => q.dataUpdatedAt).join(',');
  const statuses = useMemo(() => {
    const map = new Map<string, EntryPointStatus>();
    statusQueries.forEach((q, i) => {
      if (q.data) map.set(eps[i].id, q.data);
    });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusKey, eps]);
  const model = useMemo(
    () =>
      buildTopology(network, attachments ?? [], plan, dns, expandedGroups, { kinds: kinds.data, statuses }),
    [network, attachments, plan, dns, expandedGroups, kinds.data, statuses],
  );
  const dnsLine = dnsSummary(dns, network.settings.dns.internalZone);
  const shapes = Object.keys(network.settings.providers).map((provider) => topologyShape(network, provider));
  const empty = totalAttachments(network.attachments) === 0;
  const effectiveView = view ?? ((attachments?.length ?? 0) > LIST_DEFAULT_ABOVE ? 'list' : 'diagram');
  // A selected attachment hidden in a group card is still shown in the panel.
  const selectedItem: TopologyItem | null = selected
    ? (model.items.get(selected) ??
      (() => {
        const a = attachments?.find((x) => attachmentKey(x) === selected);
        return a
          ? {
              id: selected,
              kind: 'attachment' as const,
              level: 3,
              parentId: null,
              name: a.deploymentName,
              children: [],
              expandable: false,
              selectable: true,
              attachment: a,
            }
          : null;
      })())
    : null;

  const select = (id: string | null) => {
    const next = new URLSearchParams(search);
    if (id) next.set('select', id);
    else next.delete('select');
    setSearch(next, { replace: true });
  };
  const closePanel = () => {
    const id = selected;
    select(null);
    if (id && !phone) focusItem.current?.(id);
  };

  const checklist = (
    <GettingStarted
      network={network}
      busy={busy}
      onPlan={onPlan}
      onAttach={onAttach}
      onClose={empty ? undefined : onCloseChecklist}
    />
  );

  if (attachmentsError) return <ErrorMessage error={attachmentsError} />;
  if (!attachments) return <Loading />;

  if (empty && !phone) return <div className="space-y-4">{checklist}</div>;

  const panelNode = selectedItem && (
    <SidePanel
      network={network}
      item={selectedItem}
      plan={plan}
      dnsLine={dnsLine}
      panelRef={panel}
      onClose={closePanel}
    />
  );

  if (phone)
    return (
      <div className="space-y-3">
        {(empty || showChecklist) && checklist}
        <PhoneOverview
          network={network}
          attachments={attachments}
          plan={plan}
          dnsLine={dnsLine}
          dnsCount={dns?.zones.length ?? 0}
          selected={selected}
          onSelect={select}
        />
        {panelNode && <div className="net-sheet">{panelNode}</div>}
      </div>
    );

  return (
    <div className="space-y-4">
      {showChecklist && checklist}
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="text-[15px] font-semibold">{p.topology}</h2>
        <div role="group" aria-label={p.view} className="net-toggle">
          <button
            type="button"
            className="net-toggle-btn"
            aria-pressed={effectiveView === 'diagram'}
            onClick={() => setView('diagram')}
          >
            <LayoutGrid aria-hidden className="size-3.5" />
            {p.diagram}
          </button>
          <button
            type="button"
            className="net-toggle-btn"
            aria-pressed={effectiveView === 'list'}
            onClick={() => setView('list')}
          >
            <List aria-hidden className="size-3.5" />
            {p.list}
          </button>
        </div>
        {effectiveView === 'diagram' && (
          <ul aria-label={p.legend} className="net-legend">
            {LEGEND.map((l) => (
              <li key={l.label}>
                <span aria-hidden className={`net-dot net-dot-${l.tone}`} />
                {l.label}
              </li>
            ))}
            {shapes.some((x) => x.hub) && (
              <li>
                <span aria-hidden className="net-legend-line" />
                {tp.legendTrunk}
              </li>
            )}
            {shapes.some((x) => x.firewall) && (
              <li>
                <Shield aria-hidden className="size-3.5" />
                {tp.legendFirewall}
              </li>
            )}
            {shapes.some((x) => x.mesh) && (
              <li>
                <span aria-hidden className="net-legend-line is-dashed" />
                {tp.legendMesh}
              </li>
            )}
            {shapes.some((x) => x.requestsOnly) && (
              <li>
                <ShieldCheck aria-hidden className="net-card-shield size-3.5" />
                {tp.requestsOnly}
              </li>
            )}
          </ul>
        )}
      </div>
      {effectiveView === 'list' ? (
        <AttachmentsTab network={network} onAttach={onAttach} poll={poll} />
      ) : (
        <div className={`net-overview ${panelNode ? 'has-panel' : ''}`}>
          <TopologyTree
            network={network}
            model={model}
            selected={selected}
            dnsLine={dnsLine}
            focusRef={focusItem}
            onSelect={(id) => select(id)}
            onOpenGroup={(regionId, firstId) => {
              setExpandedGroups((g) => new Set([...g, regionId]));
              if (firstId) focusItem.current?.(firstId);
            }}
            onEnterPanel={() => panel.current?.focus()}
          />
          {panelNode}
        </div>
      )}
    </div>
  );
}

/** Phones (6.15.19): the attachments grouped by provider and region under expandable headers, as cards. */
function PhoneOverview({
  network,
  attachments,
  plan,
  dnsLine,
  dnsCount,
  selected,
  onSelect,
}: {
  network: Network;
  attachments: Attachment[];
  plan: AddressPlan | undefined;
  dnsLine: string;
  dnsCount: number;
  selected: string | null;
  onSelect: (id: string) => void;
}) {
  const [open, setOpen] = useState<ReadonlySet<string>>(() => {
    const first = providerRegions(network, attachments, plan)[0]?.regions[0];
    return new Set(first ? [`${network.id}/${first.region}`] : []);
  });
  const toggle = (key: string) =>
    setOpen((o) => {
      const next = new Set(o);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  const header = (id: string, label: string) => (
    <button
      type="button"
      className="net-phone-header"
      aria-expanded={open.has(id)}
      onClick={() => toggle(id)}
    >
      {open.has(id) ? (
        <ChevronDown aria-hidden className="size-4" />
      ) : (
        <ChevronRight aria-hidden className="size-4" />
      )}
      {label}
    </button>
  );
  const groups = providerRegions(network, attachments, plan).flatMap((prov) =>
    prov.regions.map((r) => ({ provider: prov.provider, ...r })),
  );
  return (
    <div className="net-phone" data-testid="network-phone-overview">
      <section>
        {header('entry-points', p.groupHeader(p.entryPoints, network.settings.entryPoints.length))}
        {open.has('entry-points') &&
          (network.settings.entryPoints.length === 0 ? (
            <p className="px-1 pb-2 text-[13px] text-fg-muted">
              <button type="button" className="link" onClick={() => onSelect(ENTRY_POINTS_KEY)}>
                {p.noEntryPoints}
              </button>
            </p>
          ) : (
            <ul className="space-y-2 pb-2">
              {network.settings.entryPoints.map((x) => (
                <li key={x.id}>
                  <Link className="net-ep-mini w-full" to={`/networks/${network.id}?tab=entry-points`}>
                    <span className="font-semibold">{x.id}</span>
                    <span className="text-fg-muted">
                      {x.kind === 'azure-app-gateway'
                        ? 'AGW'
                        : x.kind === 'azure-front-door'
                          ? 'AFD'
                          : x.kind}
                    </span>
                    <span className="text-fg-muted">
                      {strings.networks.entryPoints.summary(1, x.routes?.length ?? 0).split(' · ')[1]}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          ))}
      </section>
      {groups.map((g) => {
        const id = `${network.id}/${g.region}`;
        return (
          <section key={`${g.provider}/${g.region}`}>
            {header(
              id,
              p.groupHeader(p.groupProvider(providerLabel(g.provider), g.region), g.attachments.length),
            )}
            {open.has(id) && (
              <ul className="space-y-2 pb-2">
                {g.attachments.map((a) => {
                  const key = attachmentKey(a);
                  const waits = waitsForNetworkApply(a, network);
                  return (
                    <li key={key}>
                      <button
                        type="button"
                        className={`net-phone-card ${selected === key ? 'is-selected' : ''}`}
                        aria-pressed={selected === key}
                        onClick={() => onSelect(key)}
                      >
                        <span className="flex items-start justify-between gap-2">
                          <span className="font-mono font-semibold">{a.deploymentName}</span>
                          <StatusBadge presentation={attachmentPresentation(a.state, waits)} />
                        </span>
                        <span className="block font-mono text-[12.5px] text-fg-muted">
                          {a.cidr} · {a.size}
                        </span>
                        <span className="block text-[12px]">
                          {a.waitingFor ? waitingWords(a.waitingFor) : cardStateWords(a, network)}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        );
      })}
      <section>
        {header('dns', p.groupHeader(p.privateDns, dnsCount))}
        {open.has('dns') && (
          <p className="px-1 pb-2 text-[13px]">
            <button
              type="button"
              className="link"
              onClick={() => onSelect(zonesKey(Object.keys(network.settings.providers)[0] ?? 'azure'))}
            >
              {dnsLine}
            </button>
          </p>
        )}
      </section>
    </div>
  );
}
